import { useState } from "react";

type FormStatus = "idle" | "sending" | "success" | "error";

const PD = "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, sans-serif";

export default function SignupSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>).toString();
    const msg = (form.querySelector('[name="message"]') as HTMLTextAreaElement)?.value ?? "";
    if (msg.length > 500) return;
    setStatus("sending");
    try {
      const res = await fetch("https://readdy.ai/api/form/d7356f2fp7tlt849ne60", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-white py-16 md:py-24 lg:py-32" style={{ fontFamily: PD }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Info */}
          <div>
            <div className="inline-flex items-center gap-2 text-[#4B6CF7] text-xs font-medium uppercase tracking-widest mb-5 bg-[#4B6CF7]/[0.07] px-3 py-1.5 rounded-full border border-[#4B6CF7]/15">
              <i className="ri-mail-send-line text-xs"></i> Contact
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-5 leading-snug">
              기관 도입 &amp;
              <br />사전 신청 문의
            </h2>
            <p className="text-[#888888] text-sm leading-relaxed mb-8">
              대학교, 예술 고등학교, 문화예술 기관 등 교육기관의 단체 도입을 지원합니다. 커리큘럼 맞춤 구성, 강사 어드민 설정, 학생 계정 일괄 등록 서비스를 제공합니다.
            </p>
            <div className="space-y-4">
              {[
                { icon: "ri-book-open-line",  title: "대학교 / 전문대",   desc: "예술경영, 공연예술 등 관련 학과 수업 연계",        accent: "#4B6CF7" },
                { icon: "ri-building-line",   title: "문화예술 기관",     desc: "재단, 센터, 협회 등의 직원 교육 프로그램",        accent: "#7C3AED" },
                { icon: "ri-user-add-line",   title: "개인 수강생",       desc: "취준생 및 예술경영 입문자 개인 신청",              accent: "#059669" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0 mt-0.5 border"
                    style={{ backgroundColor: `${item.accent}09`, borderColor: `${item.accent}20` }}
                  >
                    <i className={`${item.icon} text-sm`} style={{ color: item.accent }}></i>
                  </div>
                  <div>
                    <p className="text-[#111111] text-sm font-semibold">{item.title}</p>
                    <p className="text-[#888888] text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white border border-black/[0.09] rounded-2xl p-7 shadow-sm">
            {status === "success" ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-emerald-100 border border-emerald-200 mx-auto mb-4">
                  <i className="ri-check-double-line text-emerald-500 text-2xl"></i>
                </div>
                <p className="text-[#111111] font-semibold text-base mb-2">문의가 접수되었습니다</p>
                <p className="text-[#888888] text-sm">영업일 2일 이내에 담당자가 연락드리겠습니다.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#555555] text-xs mb-1.5 block font-medium">이름 <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="홍길동"
                      className="w-full bg-[#F9F9F9] text-[#111111] text-sm rounded-lg px-3 py-2.5 border border-black/[0.09] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#CCCCCC]"
                      style={{ fontFamily: PD }}
                    />
                  </div>
                  <div>
                    <label className="text-[#555555] text-xs mb-1.5 block font-medium">이메일 <span className="text-red-400">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@org.kr"
                      className="w-full bg-[#F9F9F9] text-[#111111] text-sm rounded-lg px-3 py-2.5 border border-black/[0.09] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#CCCCCC]"
                      style={{ fontFamily: PD }}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[#555555] text-xs mb-1.5 block font-medium">소속 기관</label>
                  <input
                    type="text"
                    name="organization"
                    placeholder="대학교, 기관명, 단체명 등"
                    className="w-full bg-[#F9F9F9] text-[#111111] text-sm rounded-lg px-3 py-2.5 border border-black/[0.09] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#CCCCCC]"
                    style={{ fontFamily: PD }}
                  />
                </div>
                <div>
                  <label className="text-[#555555] text-xs mb-1.5 block font-medium">문의 유형</label>
                  <select
                    name="inquiry_type"
                    className="w-full bg-[#F9F9F9] text-[#111111] text-sm rounded-lg px-3 py-2.5 border border-black/[0.09] focus:outline-none focus:border-[#4B6CF7]/40 cursor-pointer"
                    style={{ fontFamily: PD }}
                  >
                    <option value="institution">기관 도입 문의</option>
                    <option value="personal">개인 수강 신청</option>
                    <option value="partnership">파트너십 제안</option>
                    <option value="etc">기타 문의</option>
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[#555555] text-xs font-medium">문의 내용</label>
                    <span className={`text-[10px] tabular-nums ${charCount > 450 ? "text-amber-500" : "text-[#BBBBBB]"}`}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="도입 목적, 예상 수강 인원, 원하시는 기업 유형 등을 자유롭게 적어주세요..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#F9F9F9] text-[#111111] text-sm rounded-lg px-3 py-2.5 border border-black/[0.09] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#CCCCCC] resize-none"
                    style={{ fontFamily: PD }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "sending" || charCount > 500}
                  className="w-full py-3 bg-[#4B6CF7] hover:bg-[#5B7CF7] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: PD }}
                >
                  {status === "sending" ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>전송 중...</>
                  ) : (
                    <><i className="ri-send-plane-fill mr-2"></i>문의 보내기</>
                  )}
                </button>
                {status === "error" && (
                  <p className="text-red-400 text-xs text-center">전송 중 오류가 발생했습니다. 다시 시도해주세요.</p>
                )}
                <p className="text-[#CCCCCC] text-[10px] text-center">영업일 2일 이내 담당자가 회신드립니다</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
